import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  extractAdminPortalData,
  useAdminModulePermission,
} from "../../../../utils/adminPortalHelpers";
import { showErrorMsg, showSuccessMsg } from "../../../../utils/Helpers";
import {
  getAdminCompanyClient,
  updateAdminCompanyClient,
} from "../../../../network/apis/adminCompanyClients";
import { buildUpdateCompanyClientPayload } from "../../../Company-portal/Clients/clientMappers";
import { buildFullInternationalPhone } from "../../../Company-portal/TeamsPermissions/employeeMappers";
import {
  adminCompanyClientRoutes,
  COMPANY_DETAILS_CLIENTS_TAB,
} from "../adminCompanyClientRoutes";

const DEFAULT_DIAL_CODE = "+966";

const getClientDialCode = (client) => client?.country?.dial_code ?? "";

const useAdminEditClient = () => {
  const history = useHistory();
  const location = useLocation();
  const { companyId, id } = useParams();
  const lang = useSelector((state) => state.locale.lang);
  const { clients, shared, inputsValidations } = messages[lang];
  const companiesList = messages[lang].companiesList;
  const { canEdit } = useAdminModulePermission(
    companyPermissionModules.manageClients,
  );

  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const resolvedCompanyId = companyId || location.state?.companyId;
  const fallbackClient = location.state?.clientData;
  const client = current || fallbackClient || null;
  const dialCode = getClientDialCode(client);

  const goToClientsTab = useCallback(() => {
    if (!resolvedCompanyId) {
      history.push(ROUTE_PATHS.companiesList);
      return;
    }
    history.push(
      adminCompanyClientRoutes.companyDetailsTabLocation(
        resolvedCompanyId,
        COMPANY_DETAILS_CLIENTS_TAB,
      ),
    );
  }, [history, resolvedCompanyId]);

  const goToViewClient = useCallback(
    (clientData) => {
      if (!resolvedCompanyId || !id) {
        goToClientsTab();
        return;
      }
      history.push({
        pathname: adminCompanyClientRoutes.view(resolvedCompanyId, id),
        state: { clientData: clientData || client, companyId: resolvedCompanyId },
      });
    },
    [history, resolvedCompanyId, id, client, goToClientsTab],
  );

  useEffect(() => {
    if (!id) {
      goToClientsTab();
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    getAdminCompanyClient(id)
      .then((response) => {
        if (cancelled) return;
        setCurrent(extractAdminPortalData(response));
      })
      .catch((error) => {
        if (cancelled) return;
        if (fallbackClient) {
          setCurrent(fallbackClient);
          showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
          return;
        }
        setCurrent(null);
        showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
        goToClientsTab();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, goToClientsTab, shared.generalErrMsg, fallbackClient]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: client?.name || "",
      email: client?.email || "",
      phone: buildFullInternationalPhone(client?.phone_number, dialCode),
      phoneCountryDialCode: String(dialCode || DEFAULT_DIAL_CODE).replace(
        /\D/g,
        "",
      ) || "966",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required("nameRequired"),
      phone: Yup.string().nullable(),
    }),
    onSubmit: async (values) => {
      if (!id || updating) return;

      setUpdating(true);
      try {
        const response = await updateAdminCompanyClient({
          id,
          data: buildUpdateCompanyClientPayload(values),
        });
        const updated = extractAdminPortalData(response);
        showSuccessMsg(clients.profileUpdatedSuccess, true);
        goToViewClient(updated || client);
      } catch (error) {
        showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
      } finally {
        setUpdating(false);
      }
    },
  });

  const breadcrumbList = useMemo(
    () => [
      { path: ROUTE_PATHS.companiesList, label: companiesList.listBreadcrumb },
      {
        path: resolvedCompanyId
          ? adminCompanyClientRoutes.companyDetailsClientsTab(resolvedCompanyId)
          : "",
        label: companiesList.clientsTab,
      },
      {
        path:
          resolvedCompanyId && id
            ? adminCompanyClientRoutes.view(resolvedCompanyId, id)
            : "",
        label: client?.name || "",
      },
      {
        path: "",
        label: clients.editProfilePageTitle || clients.editProfile,
      },
    ],
    [
      companiesList.listBreadcrumb,
      companiesList.clientsTab,
      clients.editProfile,
      clients.editProfilePageTitle,
      client?.name,
      id,
      resolvedCompanyId,
    ],
  );

  const handleCancel = useCallback(() => {
    goToViewClient();
  }, [goToViewClient]);

  return {
    clients,
    shared,
    inputsValidations,
    breadcrumbList,
    client,
    loading,
    updating,
    canEdit,
    formik,
    onCancel: handleCancel,
  };
};

export default useAdminEditClient;
